import getLogger from '#shared/utils/logger'
import type {
  NewRelicBrowserAgent,
  NewRelicLogContent,
  NewRelicServerAgent,
} from '#shared/interfaces/newRelicLogHelper'
import { LOG_LEVEL } from '#shared/constant/log'

/**
 * NewRelic へのログ送信を統一するヘルパー関数
 * 引数で受け取った情報をサーバー/クライアントに応じてそのままログ出力(NewRelicへのデータ送信)を行うことが責務。
 *
 * 以下の3つの送信経路を実行環境・エラーレベルに応じて自動で使い分ける。
 * - サーバー側: getLogger (pino) によるログ出力
 * - クライアント側: window.newrelic?.log によるログ出力
 * - エラーレベルが error の場合: noticeError による Error Inbox への記録（サーバー/クライアント両対応）
 *
 * @param logContent - ログの構造化データ
 *   呼び出し箇所ごとに出力したい内容（operationName / errorType / userId など）が異なるため、
 *   ジェネリクス型 T で呼び出し側が自由に型定義できるようにしている。
 *   pino では第1引数（オブジェクト）として構造化出力される。
 *   window.newrelic?.log では customAttributes として送信される。
 *   logContent.error に Error オブジェクトが含まれる場合、noticeError のエラー引数としても利用される。
 *   message と error の役割は明確に分離する。
 *   - message: NewRelic 上でログを分類・検索するための固定文言
 *   - error: 実際に発生したエラーの詳細（Error オブジェクトや、GraphQL の errors 配列を文字列化したものなど）
 *
 * @param serverAgent - サーバー側 New Relic SDK（省略可）
 *   `import newrelic from 'newrelic'` はサーバー専用パッケージのため、
 *   ヘルパー内でトップレベル import するとクライアント向けバンドル時にエラーになる。
 *   そのため、サーバー側の呼び出し元が newrelic を注入する設計にしている。
 *   クライアント側から呼ぶ場合は省略し、window.newrelic が自動で使われる。
 */
export const NewRelicLogHelper = <T extends Record<string, unknown>>(
  logContent: NewRelicLogContent<T>,
  serverAgent?: NewRelicServerAgent
): void => {
  const { level, category, message, detail, error } = logContent
  // shared/ はサーバー(Nitro)とクライアント両方で使われるため DOM の型(lib)を持たない。
  // window ではなく globalThis 経由で参照する(ブラウザでは window === globalThis)。
  const isServer =
    typeof (globalThis as { window?: unknown }).window === 'undefined'
  const browserAgent = (globalThis as { newrelic?: NewRelicBrowserAgent })
    .newrelic

  // クライアントで newrelic が使えない場合(ローカル等で BrowserAgent 未起動)は、
  // 送信先が無いので何もせず終了する。BrowserAgent 未起動時は NREUM の
  // 素オブジェクト(API未付与)が入り得るため、以降で使う log / noticeError が
  // 実際に関数であることを両方チェックする。ここを通過した時点で両メソッドの存在が保証される。
  if (
    !isServer &&
    (typeof browserAgent?.log !== 'function' ||
      typeof browserAgent?.noticeError !== 'function')
  ) {
    return
  }

  // サーバー側: getLogger (pino) でログ出力
  if (isServer) {
    const logger = getLogger(category)
    if (level === LOG_LEVEL.ERROR) {
      logger.error(detail as Record<string, unknown>, message)
    } else if (level === LOG_LEVEL.WARN) {
      logger.warn(detail as Record<string, unknown>, message)
    } else {
      logger.info(detail as Record<string, unknown>, message)
    }
  }

  // クライアント側: newrelic.log でログ出力
  if (!isServer) {
    browserAgent?.log?.(message, { level, customAttributes: logContent })
  }

  // エラーレベルが error: Error Inbox に記録するため noticeError を呼ぶ
  if (level === LOG_LEVEL.ERROR) {
    const err =
      error instanceof Error
        ? error
        : new Error(
            `${message}: ${typeof error === 'object' ? JSON.stringify(error) : String(error)}`
          )

    if (isServer) {
      serverAgent?.noticeError(err, detail)
      return
    }

    browserAgent?.noticeError?.(err, detail)
  }
}
